import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (!this.authService.isLoggedIn()) {
      this.authService.lastUrl = state.url;
      return this.router.createUrlTree(['/login']);
    }

    const requiredPermissions = route.data['permissions'] as string[] | undefined;
    const permissionMatch = route.data['permissionMatch'] as 'all' | 'any' | undefined;

    return this.authService.ensureCurrentUserLoaded().pipe(
      map((currentUser) => {
        if (!currentUser) {
          this.authService.lastUrl = state.url;
          return this.router.createUrlTree(['/login']);
        }

        const requiredRoles = route.data['roles'] as string[] | undefined;
        const roleMatch = route.data['roleMatch'] as 'all' | 'any' | undefined;

        if (requiredRoles && requiredRoles.length > 0) {
          const hasRequiredRole = roleMatch === 'all'
            ? this.authService.hasAllRoles(requiredRoles)
            : this.authService.hasAnyRole(requiredRoles);

          if (!hasRequiredRole) {
            return this.router.createUrlTree(['/dashboard']);
          }
        }

        if (!requiredPermissions || requiredPermissions.length === 0) {
          return true;
        }

        const isAuthorized = permissionMatch === 'any'
          ? this.authService.hasAnyPermission(requiredPermissions)
          : this.authService.hasAllPermissions(requiredPermissions);

        return isAuthorized ? true : this.router.createUrlTree(['/dashboard']);
      }),
      catchError((error) => {
        if (error.status === 401) {
          this.authService.logout();
          return of(false);
        }

        return of(this.router.createUrlTree(['/dashboard']));
      })
    );
  }
}
